import { useEffect, useRef, useState } from "react";
import { Icon } from "../icon";
import { themeVars } from "@/theme/theme.css";
import { GLOBAL_CONFIG } from "@/global-config";
import { useTranslation } from "react-i18next";

type VersionManifest = {
  buildVersion?: string;
};

const CHECK_INTERVAL = 5 * 60 * 1000;

function getVersionManifestUrl() {
  const basePath = GLOBAL_CONFIG.publicPath.endsWith("/") ? GLOBAL_CONFIG.publicPath : `${GLOBAL_CONFIG.publicPath}/`;
  return `${basePath}version.json?t=${Date.now()}`;
}

async function fetchLatestBuildVersion() {
  const response = await fetch(getVersionManifestUrl(), {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
    },
  });

  if (!response.ok) return undefined;

  const manifest = (await response.json()) as VersionManifest;
  return manifest.buildVersion;
}

function VersionUpdateNotifier() {
  const { t } = useTranslation();
  const hasUpdateRef = useRef(false);
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    const currentBuildVersion = GLOBAL_CONFIG.buildVersion;

    const checkVersion = async () => {
      try {
        const latestBuildVersion = await fetchLatestBuildVersion();

        if (!latestBuildVersion || latestBuildVersion === currentBuildVersion || hasUpdateRef.current) return;

        hasUpdateRef.current = true;
        setHasUpdate(true);
      } catch {
        // Ignore transient network failures and retry on the next interval.
      }
    };

    void checkVersion();

    const intervalId = window.setInterval(checkVersion, CHECK_INTERVAL);

    const handleVisibilityChange = () => {
      if (!document.hidden) void checkVersion();
    };

    window.addEventListener("focus", checkVersion);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkVersion);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  if (!hasUpdate) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 24,
        left: "50%",
        zIndex: 9999,
        display: "flex",
        width: "min(calc(100vw - 32px), 720px)",
        transform: "translateX(-50%)",
        alignItems: "center",
        gap: 12,
        borderRadius: 8,
        border: `1px solid ${themeVars.colors.palette.warning.default}`,
        background: themeVars.colors.background.paper,
        boxShadow: "0 16px 40px rgba(0, 0, 0, 0.22)",
        color: themeVars.colors.text.primary,
        padding: "14px 16px",
      }}
      role="alert"
    >
      <div
        style={{
          display: "flex",
          width: 36,
          height: 36,
          flex: "0 0 auto",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 8,
          background: themeVars.colors.palette.warning.lighter,
          color: themeVars.colors.palette.warning.default,
        }}
      >
        <Icon icon="carbon:warning-alt-filled" size={22} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4 }}>{t("sys.update.title")}</div>
        <div
          style={{
            marginTop: 2,
            color: themeVars.colors.text.secondary,
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          {t("sys.update.description")}
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          window.location.reload();
        }}
        style={{
          flex: "0 0 auto",
          border: 0,
          borderRadius: 8,
          background: themeVars.colors.palette.warning.default,
          color: themeVars.colors.common.white,
          cursor: "pointer",
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1,
          padding: "11px 14px",
        }}
      >
        {t("sys.update.refresh")}
      </button>
    </div>
  );
}

export default VersionUpdateNotifier;
